import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Field from 'components/forms/Field'
import { Form, Formik } from 'formik'
import moment from 'moment-timezone'
import PropTypes from 'prop-types'
import React from 'react'
import { Button, ButtonGroup, Dropdown, DropdownButton } from 'react-bootstrap'
import { FormattedDate } from 'react-intl'
import * as Yup from 'yup'

const dateFormat = 'YYYY-MM-DD'
const rangeFilters = [
  { title: 'All Time', key: 'all' },
  { title: 'Last Week', key: 'lastWeek' },
  { title: 'Last Month', key: 'lastMonth' },
  { title: 'Last Year', key: 'lastYear' },
]

export function DateFilter({ onChange, disabled }) {
  const [rangeFilter, setRangeFilter] = React.useState(rangeFilters[0].key)
  const [customRange, setCustomRange] = React.useState({
    start: '',
    end: '',
  })

  const { rangeStart, rangeEnd } = React.useMemo(() => {
    switch (rangeFilter) {
      case 'lastWeek':
        return {
          rangeStart: moment().subtract(6, 'd').format(dateFormat),
          rangeEnd: moment().format(dateFormat),
        }
      case 'lastMonth':
        return {
          rangeStart: moment().subtract(1, 'M').format(dateFormat),
          rangeEnd: moment().format(dateFormat),
        }
      case 'lastYear':
        return {
          rangeStart: moment().subtract(1, 'y').format(dateFormat),
          rangeEnd: moment().format(dateFormat),
        }
      case 'custom':
        return {
          rangeStart: customRange.start,
          rangeEnd: customRange.end,
        }
      default:
        return {
          rangeStart: null,
          rangeEnd: null,
        }
    }
  }, [rangeFilter, customRange.start, customRange.end])

  const customRangeSchemaValidation = Yup.object().shape({
    start: Yup.string()
      .required('Required')
      .matches(/^\d{4}-\d{2}-\d{2}$/, {
        message: 'Please enter a date as yyyy-mm-dd',
      })
      .test('valid date', 'Invalid date', date => moment(date).isValid()),
    end: Yup.string()
      .required('Required')
      .matches(/^\d{4}-\d{2}-\d{2}$/, {
        message: 'Please enter a date as yyyy-mm-dd',
      })
      .test('valid date', 'Invalid date', date => moment(date).isValid())
      .test(
        'after start',
        'Select date after the start date',
        (end, context) => {
          const { start } = context.parent
          return start && end && start < end
        },
      ),
  })

  const handleRangeFilter = rangeFilter => {
    setCustomRange({ start: '', end: '' })
    setRangeFilter(rangeFilter)
  }

  const handleCustomRange = values => {
    setCustomRange({ ...values })
    setRangeFilter('custom')
    // Don't love this, but I just need to close the menu once the form
    // is successfully submitted.
    document.body.click()
  }

  React.useEffect(() => {
    onChange(rangeStart, rangeEnd)
  }, [onChange, rangeStart, rangeEnd])

  return (
    <div className="d-inline-block date-filter m-1">
      <ButtonGroup>
        <DropdownButton
          as={ButtonGroup}
          variant="primary"
          id="range-filter"
          disabled={disabled}
          size="sm"
          title={
            rangeFilter === 'custom'
              ? 'CUSTOM'
              : (
                  rangeFilters.find(({ key }) => key === rangeFilter)?.title ||
                  'Unknown'
                ).toUpperCase()
          }
        >
          {rangeFilters.map(filter => (
            <Dropdown.Item
              key={filter.key}
              active={rangeFilter === filter.key}
              onClick={() => handleRangeFilter(filter.key)}
            >
              {filter.title}
            </Dropdown.Item>
          ))}
          <Dropdown.Divider />
          <Dropdown.Header>Custom</Dropdown.Header>
          <div className="px-4 pb-3 custom-range-filter">
            <Formik
              enableReinitialize
              onSubmit={handleCustomRange}
              validationSchema={customRangeSchemaValidation}
              initialValues={{ ...customRange }}
            >
              {() => (
                <Form>
                  <Field
                    label="FROM"
                    name="start"
                    type="date"
                    placeholder="yyyy-mm-dd"
                    size="sm"
                  />
                  <Field
                    label="TO"
                    name="end"
                    type="date"
                    placeholder="yyyy-mm-dd"
                    size="sm"
                  />
                  <Button block variant="primary" size="sm" type="submit">
                    Filter
                  </Button>
                </Form>
              )}
            </Formik>
          </div>
        </DropdownButton>
        {rangeStart && rangeEnd && (
          <Button
            variant="secondary"
            size="sm"
            disabled={disabled}
            onClick={() => handleRangeFilter('all')}
          >
            <FormattedDate
              value={rangeStart}
              day="numeric"
              month="short"
              year="numeric"
              timeZone="utc"
            />
            <FontAwesomeIcon
              icon={['far', 'long-arrow-right']}
              fixedWidth
              className="mx-1"
            />
            <FormattedDate
              value={rangeEnd}
              day="numeric"
              month="short"
              year="numeric"
              timeZone="utc"
            />
            <FontAwesomeIcon icon="times" fixedWidth className="ml-2" />
          </Button>
        )}
      </ButtonGroup>
    </div>
  )
}

DateFilter.propTypes = {
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
}

DateFilter.defaultProps = {
  disabled: false,
  onChange: () => {},
}

export default DateFilter
